import { getIn, useFormikContext } from 'formik'
import type { FieldConfig as FormikFieldConfig } from 'formik/dist/Field'
import { useRef } from 'react'

import { dateAsUTC } from '@utils/date.utils'

import { Box } from '@shared/components/Box'

import { DatePicker, DatePickerValue } from '../datePicker/DatePicker'
import { DeepNonNullable } from '../types/DeepNonNullable'
import type { DeepRequired } from '../types/DeepRequired'
import type { TypedFieldProps } from '../types/TypedFieldProps'
import type { ValidateField } from '../types/ValidateField'
import { getFieldValid, getValue } from './helpers'

type FieldConfig = Omit<FormikFieldConfig, 'name'>
interface FormFieldProps extends FieldConfig {
  namePrefix?: string
  name: string
  helpText?: string
  highlighted?: boolean
  value?: DatePickerValue
  onChange?(value: string | undefined | null): void
  onClick?(): void
  maxDate?: Date
  showYearDropdown?: boolean
}

export function RawFormField<FormValues>(
  props: FormFieldProps & Omit<TypedFieldProps<FormValues>, 'name'>
) {
  const { name = '', onChange, ...inputProps } = props

  const fieldRef = useRef<HTMLDivElement>(null)
  const context = useFormikContext<FormValues>()

  const value = getValue(context.values, props.value, name) as DatePickerValue

  const error = getIn(context.errors, name)
  const touched = getIn(context.touched, name)

  const isValid = getFieldValid(error, touched)

  const handleFieldTouched = () => {
    context.setFieldTouched(name, true)
  }

  const handleFieldChange = (date: Date | undefined | null) => {
    handleFieldTouched()

    if (!date) {
      if (onChange) {
        onChange(null)
      } else {
        context.setFieldValue(name, null)
      }
      return
    }

    const isoDate = dateAsUTC(date).toISOString()
    if (onChange) {
      onChange(isoDate)
    } else {
      context.setFieldValue(name, isoDate || null)
    }
  }

  return (
    <Box ref={fieldRef}>
      <DatePicker
        {...inputProps}
        value={value}
        error={!isValid ? error : undefined}
        disabledKeyboardNavigation
        showYearDropdown
        onChange={handleFieldChange}
        onBlur={handleFieldTouched}
      />
    </Box>
  )
}

export function createFormDatePickerField<FormValues>() {
  return function TypedField<Name extends ValidateField<DeepNonNullable<DeepRequired<FormValues>>>>(
    props: FormFieldProps & TypedFieldProps<DeepNonNullable<DeepRequired<FormValues>>, Name>
  ) {
    return <RawFormField {...props} />
  }
}
